import { useCallback, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import ResourceBar from "@/components/ResourceBar";
import { DailyTrophyBonusButton } from "@/components/DailyTrophyBonusButton";
import { usePrivy } from "@privy-io/react-auth";
import { MarketCountdown } from "@/components/TokenSelectionSheetContent";
import configs from "@/lib/configs";
import { useSseQuery } from "@/lib/hooks/useSseQuery";
import { useStockList, useTokenList } from "@/lib/hooks/useApi";
import { TokenDashboardHeader } from "./-components/TokenDashboardHeader";
import { TokenTableView } from "./-components/TokenTableView";
import { TokenTreemapView } from "./-components/TokenTreemapView";
import { TokenDetail } from "./-components/TokenDetail";
import type {
  TokenInterval,
  TokenPerformance,
  TokenDashboardData,
  AssetFilter,
} from "@/lib/types/token-dashboard";

export const Route = createFileRoute("/(game)/terminal/")({
  component: RouteComponent,
});

const FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.15 },
} as const;

const SSE_INITIAL: TokenDashboardData = {
  gainers: [],
  losers: [],
  updatedAt: 0,
};
const SSE_ON_EVENT = (_type: string, d: TokenDashboardData) => d;

function RouteComponent() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const [view, setView] = useState<"table" | "treemap">("table");
  const [interval, setInterval] = useState<TokenInterval>("1m");
  const [search, setSearch] = useState("");
  const [searchError, setSearchError] = useState("");
  const [assetFilter, setAssetFilter] = useState<AssetFilter>("all");
  const [limit, setLimit] = useState(20);
  const [selectedToken, setSelectedToken] = useState<TokenPerformance | null>(
    null,
  );

  const { data } = useSseQuery<TokenDashboardData>({
    url: `${configs.PRICE_INGESTOR_URL}/sse/token-dashboard?assetType=all&limit=${limit}`,
    queryKey: ["sse", "token-dashboard", limit],
    initialData: SSE_INITIAL,
    onEvent: SSE_ON_EVENT,
    debugLabel: "SSE token-dashboard",
  });
  const { gainers, losers } = data;

  // Token metadata for search lookup + stock market status
  const { data: cryptoTokens } = useTokenList(1, 120, "crypto");
  const { data: stocksData } = useStockList(1, 120);
  const allTokenMeta = useMemo(
    () => [
      ...(cryptoTokens ?? []).map((t) => ({
        ...t,
        assetType: "crypto" as const,
      })),
      ...(stocksData?.tokens ?? []).map((t) => ({
        ...t,
        assetType: "stocks" as const,
      })),
    ],
    [cryptoTokens, stocksData],
  );

  const marketStatus = stocksData?.marketStatus ?? null;
  const stockMarketClosed = !!marketStatus && !marketStatus.isOpen;
  const showMarketBanner =
    marketStatus &&
    (selectedToken
      ? selectedToken.assetType === "stocks"
      : assetFilter === "stocks");

  const filtered = useMemo(() => {
    const filter = (list: TokenPerformance[]) =>
      list.filter((t) => assetFilter === "all" || t.assetType === assetFilter);

    // Re-sort by selected interval's PnL (null sorts to end)
    const sortByPnl = (list: TokenPerformance[], desc: boolean) =>
      [...filter(list)].sort((a, b) => {
        const ap = a.intervals[interval].pnl;
        const bp = b.intervals[interval].pnl;
        if (ap === null && bp === null) return 0;
        if (ap === null) return 1;
        if (bp === null) return -1;
        return desc ? bp - ap : ap - bp;
      });

    return {
      gainers: sortByPnl(gainers, true),
      losers: sortByPnl(losers, false),
    };
  }, [gainers, losers, assetFilter, interval]);

  // Search suggestions — filter token metadata as user types
  const searchSuggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allTokenMeta
      .filter(
        (t) =>
          t.tokenSymbol.toLowerCase().includes(q) ||
          t.tokenName.toLowerCase().includes(q),
      )
      .slice(0, 8)
      .map((t) => ({
        ticker: t.tokenSymbol,
        name: t.tokenName,
        playerCard: t.playerCard,
        assetType: (t.assetType ?? "crypto") as "crypto" | "stocks",
      }));
  }, [search, allTokenMeta]);

  // When user taps a suggestion
  const handleSelectSuggestion = useCallback(
    (ticker: string) => {
      const meta = allTokenMeta.find(
        (t) => t.tokenSymbol.toLowerCase() === ticker.toLowerCase(),
      );
      if (!meta) return;
      const isStock = (meta.assetType ?? "crypto") === "stocks";
      if (isStock && stockMarketClosed) {
        setSearchError("Market is closed");
        return;
      }
      const token: TokenPerformance = {
        ticker: meta.tokenSymbol,
        name: meta.tokenName,
        assetType: (meta.assetType ?? "crypto") as "crypto" | "stocks",
        playerCard: meta.playerCard,
        pythLazerId: meta.pythLazerId,
        currentPrice: 0,
        intervals: {
          "1m": { pnl: null },
          "5m": { pnl: null },
          "10m": { pnl: null },
          "30m": { pnl: null },
          "4h": { pnl: null },
        },
      };
      setSelectedToken(token);
      setSearch("");
      setSearchError("");
    },
    [allTokenMeta, stockMarketClosed],
  );

  // Search: lookup ticker from token list metadata, open detail
  const handleSearchSubmit = useCallback(() => {
    const q = search.trim().toLowerCase();
    if (!q) return;
    setSearchError("");

    // First check dashboard data
    const dashboardMatch = [...gainers, ...losers].find(
      (t) => t.ticker.toLowerCase() === q,
    );
    if (dashboardMatch) {
      if (dashboardMatch.assetType === "stocks" && stockMarketClosed) {
        setSearchError("Market is closed");
        return;
      }
      setSelectedToken(dashboardMatch);
      setSearch("");
      return;
    }

    // Fall back to token list metadata
    const meta = allTokenMeta.find(
      (t) =>
        t.tokenSymbol.toLowerCase() === q || t.tokenName.toLowerCase() === q,
    );
    if (meta) {
      const isStock = (meta.assetType ?? "crypto") === "stocks";
      if (isStock && stockMarketClosed) {
        setSearchError("Market is closed");
        return;
      }
      const token: TokenPerformance = {
        ticker: meta.tokenSymbol,
        name: meta.tokenName,
        assetType: (meta.assetType ?? "crypto") as "crypto" | "stocks",
        playerCard: meta.playerCard,
        pythLazerId: meta.pythLazerId,
        currentPrice: 0,
        intervals: {
          "1m": { pnl: null },
          "5m": { pnl: null },
          "10m": { pnl: null },
          "30m": { pnl: null },
          "4h": { pnl: null },
        },
      };
      setSelectedToken(token);
      setSearch("");
      return;
    }

    setSearchError("Token not found");
  }, [search, gainers, losers, allTokenMeta, stockMarketClosed]);

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-[#1D2A52] relative">
      {/* Absolute nav — always visible */}
      <FloatingLeftButton
        onClick={() => navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      <ResourceBar
        showBalance
        showLuckyDrawTicket
        showMenu
        className="absolute top-3 left-12 right-4 flex z-20 pl-3"
      />
      {!!user && (
        <div className="absolute top-16 right-4 z-30 flex flex-col items-center">
          <DailyTrophyBonusButton source="terminal" />
        </div>
      )}

      {/* Header — always visible, interval row hidden when detail open */}
      <TokenDashboardHeader
        view={view}
        onViewChange={(v) => {
          setView(v);
          setSelectedToken(null);
        }}
        interval={interval}
        onIntervalChange={setInterval}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setSearchError("");
        }}
        onSearchSubmit={handleSearchSubmit}
        searchError={searchError}
        searchSuggestions={searchSuggestions}
        onSelectSuggestion={handleSelectSuggestion}
        stockMarketClosed={stockMarketClosed}
        assetFilter={assetFilter}
        onAssetFilterChange={setAssetFilter}
        limit={limit}
        onLimitChange={setLimit}
        hideIntervals={!!selectedToken}
      />

      <div className="border-t border-white/10" />

      {/* Market status banner */}
      {showMarketBanner && (
        <div className="px-2">
          <MarketCountdown
            marketStatus={marketStatus}
            label={
              marketStatus.isOpen
                ? "Stocks unavailable in:"
                : "Stocks available in:"
            }
            description={
              marketStatus.isOpen
                ? undefined
                : "*Stocks will only visible when markets open"
            }
          />
        </div>
      )}

      {/* Content area — swaps between list/detail, header stays */}
      <AnimatePresence mode="wait">
        {selectedToken ? (
          <motion.div
            key="detail"
            {...FADE}
            className="flex-1 min-h-0 flex flex-col overflow-y-auto"
          >
            <TokenDetail
              token={selectedToken}
              initialInterval={interval}
              onClose={() => setSelectedToken(null)}
            />
          </motion.div>
        ) : view === "table" ? (
          <motion.div
            key="table"
            {...FADE}
            className="flex-1 min-h-0 flex flex-col"
          >
            <TokenTableView
              gainers={filtered.gainers}
              losers={filtered.losers}
              interval={interval}
              isLoading={data.updatedAt === 0}
              stockMarketClosed={stockMarketClosed}
              onSelectToken={setSelectedToken}
            />
          </motion.div>
        ) : (
          <motion.div
            key="treemap"
            {...FADE}
            className="flex-1 min-h-0 flex flex-col"
          >
            <TokenTreemapView
              gainers={filtered.gainers}
              losers={filtered.losers}
              interval={interval}
              isLoading={data.updatedAt === 0}
              stockMarketClosed={stockMarketClosed}
              onSelectToken={setSelectedToken}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
